import React, {
  useMemo,
  useContext,
  useState,
  useCallback,
} from 'react';
import { IColumnDef } from '../../Types/ColumnDefType';
import EDViewportWidthContainer from '../../BaseComponents/ViewportWidthContainer/EDViewportWidthContainer';
import {
  ModelContext,
} from '../../Model/ModelContext/ModelContext';
import KolumnvaljareLists from './Components/KolumnvaljareLists/KolumnvaljareLists';
import {
  fillSublists,
  applyCheckedState,
} from './Utils/kolumnvaljarKolumnUtils';
import ChipRadioButtons, {
  IChipChoice,
} from '../ChipRadioButtons/ChipRadioButtons';
import { getUnpickedColumns } from './Utils/getFields';
import { TVisningstyp } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataSet/BusinessDataSet';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../BaseComponents/Typography/EDTypography';
import EDCheckbox from '../../BaseComponents/Checkboxes/EDCheckbox';
import { useIsStyrvarde } from '../../Hooks/useIsStyrvarde';
import useShowDetailsRightDisabledOnCurrentScreenSize from '../../Features/Delsystem/_StandardRutin/Hooks/useShowDetailsRightDisabledOnCurrentScreenSize';
import { visningstyper } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataSet/Visningstyper';
import { ITreeSection } from '../../BaseComponents/TreeSelect/TreeSelectUtils';
import { useWidth } from '../BreakpointContext/BreakpointContextUtils';
// Kolumner i Kolumnväljaren kan ha 4 olika typer (BaseCol, OwnNameCol, ExpandableCol och ItemType)
interface ICol {
  id: string;
  path: string;
  text: string;
  ownName?: string;
  header?: boolean;
  selected?: boolean;
}

export interface IBaseCol extends ICol {
  fieldType: 'attribute';
  group?: string;
}

export interface ISectionData {
  fieldType: 'attribute' | 'expandable';
  path: string;
  isExpandable: boolean;
  isAttributeGroup: boolean;
  associatedCollectionName?: string;
  associatedComponentName?: string;
  header?: boolean;
  ownName?: string;
}

export interface IExpandableCol extends ICol {
  fieldType: 'expandable';
  id: string;
  path: string;
  text: string;
  isExpandable: boolean;
  associatedCollectionName: string;
  associatedComponentName: string;
  isAttributeGroup: boolean;
  subList: Array<IBaseCol | IExpandableCol>;
  open: boolean;
  selectedChildren: number;
  selectedHeaderChildren: number;
}

export type KolumnvaljarColumnType = IBaseCol | IExpandableCol;

export interface IKolumnValjarProps {
  /** De kolumner som är valda just nu */
  valdaKolumner: readonly IColumnDef[];
  /** Namnet på collectionen som kolumnväljaren ska hämta kolumnerna ifrån */
  collectionName: string;
  /** Callback som kallas när man trycker på OK-knappen
   * @param {Array} valdaKolumner - en lista med de nya valda kolumnerna
   */
  setValdaKolumner: (valdaKolumner: readonly IColumnDef[]) => void;
}

interface IProps extends IKolumnValjarProps {
  visningstyp: TVisningstyp;
  setVisningstyp: (visningstyp: TVisningstyp) => void;
  showDetailsRight: boolean;
  setShowDetailsRight: (value: boolean) => void;
  showDetailsRightDisabled: boolean;
  inCard?: boolean;
  visningsTypShowDetailsRight?: TVisningstyp;
  defaultVisningsTyp?: TVisningstyp;
  selectedKolumnSettingId: string | null;
  showDevOptions?: boolean;
}

const useStyles = makeStyles()(
  theme =>
    ({
      container: {
        minHeight: '500px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      containerSpacing: {
        marginBottom: 32,
      },
      visningstypHeaderContainer: {
        display: 'flex',
        alignItems: 'center',
      },
    } as const)
);

/**
 * En dialog där kolumner och visningstyp för en standardrutin kan ändras.
 */
const KolumnValjare: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { setValdaKolumner } = props;
  const { classes } = useStyles();
  const modelContext = useContext(ModelContext);

  // Base tree structure - only recomputes when model or collection changes
  const allColumns = useMemo(() => {
    let kolumner = getUnpickedColumns(
      modelContext.avenyCollectionMap,
      [],
      props.collectionName,
      null
    );
    return fillSublists(modelContext, kolumner, []);
  }, [modelContext, props.collectionName]);

  // UI state (open/closed, hidden/visible) - NOT reset when valdaKolumner changes
  const [columnSections, setColumnSections] = useState<
    ITreeSection<ISectionData>[]
  >(() => allColumns);


  // Display tree: combines UI state with selection state
  const displaySections = useMemo(
    () => applyCheckedState(columnSections, props.valdaKolumner),
    [columnSections, props.valdaKolumner]
  );

  const [searchValue, setSearchValue] = useState('');

  const headerColumns = useMemo(
    () => props.valdaKolumner.filter(x => x.header),
    [props.valdaKolumner]
  );
  const nonHeaderColumns = useMemo(
    () => props.valdaKolumner.filter(x => !x.header),
    [props.valdaKolumner]
  );
  const setActiveColumns = useCallback(
    (columns: IColumnDef[]) => {
      setValdaKolumner(headerColumns.concat(columns));
    },
    [headerColumns, setValdaKolumner]
  );
  const selectionUpdate = useCallback(
    (
      sections: ITreeSection[],
      section: ITreeSection,
      selected: boolean,
      column?: ITreeSection
    ) => {
      const updateSection = column ?? section;
      const valdaKolumner = updateSection
        ? selected
          ? [
              ...props.valdaKolumner,
              {
                attributeDef: updateSection.value,
                caption: updateSection.name,
              },
            ]
          : props.valdaKolumner.filter(
              x => x.attributeDef !== updateSection.value
            )
        : props.valdaKolumner;

      if (updateSection?.data.fieldType === 'attribute') {
        setValdaKolumner(valdaKolumner);
      }
    },
    [props.valdaKolumner, setValdaKolumner]
  );

  const width = useWidth();
  const showDetailsRightDisabledOnCurrentScreenSize =
    useShowDetailsRightDisabledOnCurrentScreenSize();
  const isStyrvarde = useIsStyrvarde();
  const showDetailRightCheckBoxDisabledReason = props.showDetailsRightDisabled
    ? `Visning till höger ej valbart för ${
        isStyrvarde ? 'detta styrvärde' : 'denna rutin'
      }`
    : showDetailsRightDisabledOnCurrentScreenSize
    ? 'Visning till höger ej valbart på smal skärm'
    : undefined;

  const visningsTypShowDetailsRight = props.visningsTypShowDetailsRight;
  const defaultVisningsTyp: TVisningstyp = props.defaultVisningsTyp ?? 'tabell';
  const visningstypsval: IChipChoice<TVisningstyp>[] = visningstyper
    .filter(
      typ =>
        (typ.defaultAvailable && !(!!typ.disableInCard && props.inCard)) ||
        props.showDevOptions ||
        typ.id === props.visningstyp
    )
    .map(visningstyp => ({
      value: visningstyp.id,
      label: visningstyp.label,
      icon: visningstyp.icon,
    }));

  return (
    <EDViewportWidthContainer classes={{ container: classes.container }}>
      {props.setVisningstyp && (
        <>
          <div className={classes.visningstypHeaderContainer}>
            <EDTypography bold>Visningstyp</EDTypography>
            <EDCheckbox
              checkboxSpacing={{ marginLeft: 4 }}
              label={'Visa detaljer till höger'}
              checked={props.showDetailsRight}
              onCheck={checked => {
                props.setShowDetailsRight(checked);
                if (checked && visningsTypShowDetailsRight) {
                  props.setVisningstyp(visningsTypShowDetailsRight);
                } else if (checked && props.visningstyp !== 'lista') {
                  props.setVisningstyp('lista');
                } else if (
                  !checked &&
                  props.visningstyp !== defaultVisningsTyp &&
                  width !== 'xs' &&
                  width !== 'sm'
                ) {
                  props.setVisningstyp(defaultVisningsTyp);
                }
              }}
              disabled={
                props.showDetailsRightDisabled ||
                showDetailsRightDisabledOnCurrentScreenSize
              }
              disabledReason={showDetailRightCheckBoxDisabledReason}
            />
          </div>
          <ChipRadioButtons
            value={props.visningstyp}
            setValue={props.setVisningstyp}
            choices={visningstypsval}
            className={classes.containerSpacing}
          />
        </>
      )}
      <KolumnvaljareLists
        headerColumns={headerColumns}
        pickedColumns={nonHeaderColumns}
        unpickedColumns={displaySections}
        setUnpickedColumns={setColumnSections}
        setActiveColumns={setActiveColumns}
        selectionUpdate={selectionUpdate}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
    </EDViewportWidthContainer>
  );
};

export default KolumnValjare;
